import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MessageSquarePlus, X, ImagePlus, Search, Send, Loader2, CircleCheck, CircleX, Clock, Bug, Paperclip, ArrowRight, RefreshCw, RotateCcw, FileText, FileSpreadsheet, Film, Lightbulb } from "lucide-react";
import { useApp } from "../context/AppContext";
import { authStore } from "../lib/api";
import { toast } from "./ui/sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { CrmDetailDrawer } from "./crm/CrmDetailDrawer";
import { MediaLightbox, mediaKind } from "./MediaLightbox";
import {
  ATTACH_ACCEPT, ATTACH_COUNT_MAX, ATTACH_MAX, fmtBytes, isCanceled, resolveAtt,
  uploadAttachment, uploadErrText,
} from "../lib/attach";

/**
 * FeedbackButton —— 顶部栏「需求反馈」入口（铃铛后方）。
 *
 * 需求来源（2026-10-14，老板口述，见 backend/feedback.py 文件头）：
 *   ① 顶部栏铃铛后方新增入口，点击后可提交问题反馈 / Bug；
 *   ② 提交内容 = 需求描述（必填）+ 想要的效果（必填）+ 附件（非必填）+ 问题页面（非必填）；
 *   ③ 提交时静默带上当前账号标识与登录 token（便于管理员复现），并提供可选的「账号密码」输入；
 *   ④ 按钮上有「未读回复数」角标（管理员回复/状态变更后 +1，点开详情即清零）；
 *   ⑤ 点开面板可看到「我提交过的反馈列表」（描述 + 状态），点条目开抽屉看详情与双方聊天记录。
 *
 * 追加需求与修复（2026-10-14，用户反馈）：
 *   ⑥ 截图区域除「点击选择 / 拖入」外，支持直接把剪贴板里的图片 Ctrl+V 粘贴进来
 *      （用截图工具 / 微信截图后不必先存盘再选文件）；提交表单与详情抽屉回复框都支持。
 *   ⑦ 修复：提交表单的「问题页面」输入框聚焦时会弹出 Chrome 密码管理器的账号下拉
 *      （admin / Rosy / 管理密码…）。根因：该 input 未声明 autocomplete，且 DOM 上紧邻
 *      下方 `type="password"` 的可选密码框，Chrome 便把这组输入识别成登录表单，
 *      把「问题页面」当成用户名框。改法见 ComposeDialog 内两处 input 的注释。
 *   ⑧ 修复：从顶栏「我的反馈」下拉点条目时详情抽屉打不开（看着像「点了没反应」）。
 *      根因：顶栏 `.app-topbar` 带 `backdrop-filter: blur(12px)`，而 backdrop-filter 会成为
 *      其 `position: fixed` 后代的 containing block，抽屉根节点的 inset-0 被压成「顶栏那一条」。
 *      改法：FeedbackDrawer 用 createPortal 渲染到 body，详见 FeedbackButton 内该处注释。
 *      菜单里的页面（/admin/feedback）祖先无毛玻璃，抽屉一直正常，无需改动。
 *   ⑨ 列表与详情显示反馈 ID（2026-10-15，用户需求「需求反馈列表和右上角的反馈面板列表里
 *      增加当前 bug 的 id 的显示」）：顶栏面板列表项、管理端汇总列表项、详情抽屉头部三处
 *      均显示完整 ID（`FB` + 10 位十六进制 = 12 字符，见 backend/feedback.py 的 fid 生成），
 *      点击即复制到剪贴板。ID 是查库 / 翻日志 / 截图沟通的唯一凭据，故**完整展示不截断**。
 *      实现见文件内导出的 IdChip 组件（含 navigator.clipboard 不可用时的 execCommand 兜底）。
 *
 * 追加（2026-10-23，用户反馈「用户端现在好像有重新开启按钮，在需求反馈这里也没显示出来」）：
 *   ① 「我的反馈」下拉面板原先只有状态胶囊 + ID + 时间，看不出「这条是我自己重开回来的」。
 *      现在重开过的条目在面板里也带品牌色「重新开启」徽章（与管理端列表同一套语义），
 *      用户扫一眼就知道哪条是他重新拉回来、正在等管理员处理的。
 *   ② 面板列表**分页**：以前 `/feedback/mine` 一次返回最多 300 条，面板高 50vh 也只能
 *      滚着看、且一次拉全部文档（含 messages 数组）很慢。现在面板底部有「共 N 条 · x/y」
 *      与上/下一页，切页只拉 20 条。
 *      为什么面板要做分页而不是「加载更多」：面板是浮层，高度固定、可滚区域有限，
 *      分页能同时给出「一共多少条」这个用户真正关心的信息。
 *
 * 设计对齐：下拉面板沿用顶栏铃铛（Layout.jsx topbar-bell-dropdown）的写法 ——
 *   createPortal 全屏遮罩 + absolute right-0 top-11 面板；详情抽屉复用 CRM 的 CrmDetailDrawer
 *   （右侧抽屉壳，自带宽度拖拽 / Esc 关闭 / 层级 z-45，不会压住 Radix 弹窗）。
 *
 * @param {Array<{to:string,label:string}>} pages 供「问题页面」搜索选择的菜单项（由 Layout 传入 flatNav）
 * @param {Function} onOpenChange 面板开合回调（父组件可据此做联动，可空）
 *
 * 附件链路改造（2026-10-15，用户需求「图片/视频/PDF/表格等都能提交；图片视频要压缩；
 * 大文件切片上传；查看时页内预览不跳外部标签页」）：
 *   · 附件不再走 multipart 直传，改为「前端压缩 → 切片上传 → 提交 fileId」（见 useAttachments）；
 *   · 附件读取直链统一用 `lib/attach.js` 的 fileUrl（走 authStore，兼容「仅本次会话」登录）；
 *   · 预览统一用 `MediaLightbox`（页内全屏层），不再 `target="_blank"` 开新标签页。
 */

/**
 * 「我的反馈」面板每页条数（2026-10-23，用户需求「需求反馈没有做分页」）。
 * 取 10 而不是管理端的 20：面板可视高度只有 50vh（约 360px），一条约 60px，
 * 10 条刚好一屏多一点，翻页频率低、又不会一打开就要滚。
 */
const MINE_PAGE_SIZE = 10;

/**
 * 从剪贴板数据里提取图片文件（供「Ctrl+V 粘贴截图」使用，见文件头需求 ⑥）。
 *
 * 为什么这么写：
 *  ① Chrome 粘贴图片时，文件主要挂在 `clipboardData.items`（kind === "file"），少数场景只在
 *     `files` 里 —— 两处都兜一遍，避免「截图工具复制的图识别不到」。
 *  ② 粘贴命名的文件常常没有名字（或统一叫 image.png），而后端 `feedback.py:_save_files`
 *     按扩展名白名单校验，无名文件会被判为「不支持的文件类型」直接 400。
 *     因此这里统一补一个带时间戳的规范文件名，与 chat/Composer.jsx 的粘贴处理保持一致。
 */
const pickClipboardImages = (dt) => {
  if (!dt) return [];
  const out = [];
  Array.from(dt.items || []).forEach((it) => {
    if (it.kind === "file" && (it.type || "").startsWith("image/")) {
      const f = it.getAsFile();
      if (f) out.push(f);
    }
  });
  if (!out.length) out.push(...Array.from(dt.files || []).filter((f) => (f.type || "").startsWith("image/")));
  const stamp = Date.now();
  return out.map((f, i) => (
    f.name && f.name !== "image.png"
      ? f
      : new File([f], `pasted_${stamp}_${i}.${(f.type.split("/")[1] || "png").replace("jpeg", "jpg")}`, { type: f.type })
  ));
};

/* ------------------------------------------------------------------ */
/* 附件：选择 → 压缩 → 切片上传 → 页内预览（提交表单 / 详情回复框共用） */
/* ------------------------------------------------------------------ */

// 并行上传条数：一次选 8 张图串行传，总耗时是并行的 3 倍。3 是「够快」与「不把后端打满」的折中。
const UPLOAD_CONCURRENCY = 3;

/**
 * 附件选择 + 上传的共用逻辑（2026-10-15 需求：图片 / 视频 / PDF / 表格等都能提交，
 * 图片与视频要压缩、大文件要切片上传、查看时页内预览不跳外部标签页）。
 *
 * 关键点：
 *  ① **切片上传**：走 `lib/upload.js` 的 uploadChunked（2MB 分片 / 断点续传 / 分片重试 /
 *     SHA-256 校验 / 200MB 上限）。以前反馈是 multipart 直传，一张 10MB 手机原图在弱网下
 *     传不动，失败了还要整包重来。
 *  ② **前端压缩**：`uploadAttachment` 走 shot 档（长边 1600 ≈ 300KB）—— 这才是治
 *     「上传慢」的那一环，传出去的字节数直接少一个数量级。
 *  ③ **并行**：最多同时传 3 个，多附件时总时长不会被串行拖满。
 *  ④ 上传成功的只留 fileId，提交时随 JSON 一起给后端（后端再权威校验归属 / 类型 / 大小）。
 */
function useAttachments({ api, zh, max = ATTACH_COUNT_MAX }) {
  const [atts, setAtts] = useState([]);    // 已传成功：[{fileId,filename,contentType,size,kind}]
  const [jobs, setJobs] = useState([]);    // 进行中/失败：[{key,file,name,pct,state,error}]
  const ctrlRef = useRef(null);

  const addFiles = useCallback(async (fl) => {
    const list = Array.from(fl || []);
    if (!list.length) return;
    const room = Math.max(0, max - atts.length - jobs.length);
    if (!room) { toast.error(zh ? `最多 ${max} 个附件，请先删掉一些再添加` : `Up to ${max} attachments`); return; }
    const picked = list.slice(0, room);
    if (picked.length < list.length) toast.error(zh ? `最多 ${max} 个，已忽略 ${list.length - picked.length} 个` : `Up to ${max} files`);
    picked.filter((f) => f.size > ATTACH_MAX).forEach((f) =>
      toast.error(zh ? `「${f.name}」超过 ${Math.round(ATTACH_MAX / 1024 / 1024)}MB，已跳过` : `${f.name} exceeds the size limit`));
    const use = picked.filter((f) => f.size <= ATTACH_MAX);
    if (!use.length) return;

    const ctrl = new AbortController();
    ctrlRef.current = ctrl;
    const stamp = Date.now();
    const items = use.map((f, i) => ({ key: `${stamp}_${i}`, file: f, name: f.name, pct: 0, state: "queued", error: "" }));
    setJobs((cur) => [...cur, ...items]);
    const upd = (key, patch) => setJobs((cur) => cur.map((j) => (j.key === key ? { ...j, ...patch } : j)));

    let cursor = 0;
    const worker = async () => {
      while (cursor < items.length) {
        const it = items[cursor++];
        upd(it.key, { state: "preparing" });
        try {
          const r = await uploadAttachment(api, it.file, {
            signal: ctrl.signal,
            onProgress: ({ pct, state }) => upd(it.key, { pct, state }),
          });
          setAtts((cur) => [...cur, { fileId: r.fileId, filename: r.filename, contentType: r.contentType, size: r.size, kind: r.kind }]);
          setJobs((cur) => cur.filter((j) => j.key !== it.key));
        } catch (e) {
          if (isCanceled(e)) { setJobs((cur) => cur.filter((j) => j.key !== it.key)); return; }
          upd(it.key, { state: "error", error: uploadErrText(e, zh) });
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(UPLOAD_CONCURRENCY, items.length) }, worker));
    ctrlRef.current = null;
  }, [api, zh, max, atts.length, jobs.length]);

  const cancel = () => { ctrlRef.current?.abort(); toast.info(zh ? "已取消上传" : "Upload canceled"); };
  const reset = useCallback(() => { setAtts([]); setJobs([]); }, []);
  const uploading = jobs.some((j) => j.state !== "error");
  // 失败的重试：把文件重新丢回上传队列
  const retry = (job) => { setJobs((cur) => cur.filter((j) => j.key !== job.key)); addFiles([job.file]); };

  return { atts, jobs, addFiles, cancel, reset, uploading, retry, setAtts, setJobs };
}

const STATE_TEXT = {
  queued: "排队中", preparing: "压缩中", uploading: "上传中", retrying: "重试中",
  offline: "网络中断，等待恢复", hashing: "校验中", finalizing: "合并中", error: "失败",
};

const KindIcon = ({ kind, className = "h-5 w-5" }) => {
  if (kind === "video") return <Film className={className} />;
  if (kind === "sheet") return <FileSpreadsheet className={className} />;
  return <FileText className={className} />;
};

/**
 * 附件网格：已传的显示缩略图 / 图标（点开**页内**预览），上传中的显示进度与百分比，
 * 失败的给「重试」。抽出来是因为提交表单和回复框要用同一套。
 *
 * ⚠️ `jobs` 默认必须给空数组：详情抽屉里只展示**已存在**的附件（没有「上传中」这个概念），
 *    调用处不会传 jobs —— 2026-10-15 真机实测踩到过，漏了默认值就直接
 *    `Cannot read properties of undefined (reading 'map')` 把详情抽屉整个打崩。
 */
function AttachGrid({ atts, jobs = [], media, onRemove, onOpen, compact = false, onRetry, zh }) {
  const list = atts || [];
  const pending = jobs || [];
  const size = compact ? "h-14 w-14" : "h-20 w-20";
  if (!list.length && !pending.length) return null;
  return (
    <div className="flex flex-wrap gap-2" data-testid="feedback-attach-grid">
      {list.map((a, i) => {
        const v = resolveAtt(a, media);
        const isImg = v.kind === "image";
        return (
          <div key={v.fileId} className={`relative group ${size} rounded-lg overflow-hidden border border-slate-200 bg-white`}>
            <button type="button" onClick={() => onOpen?.(i)} title={zh ? "页内预览" : "Preview"}
              className="h-full w-full flex items-center justify-center text-slate-400" data-testid={`feedback-attach-${v.fileId}`}>
              {isImg
                ? <img src={v.url} alt={v.name} className="h-full w-full object-cover" />
                : <span className="flex flex-col items-center gap-0.5 px-1"><KindIcon kind={v.kind} /><span className="text-[9px] leading-tight max-w-full truncate">{v.name}</span></span>}
            </button>
            {v.processing && (
              <span className="absolute inset-x-0 bottom-0 bg-sky-600/90 text-white text-[9px] text-center py-0.5" data-testid={`feedback-attach-processing-${v.fileId}`}>
                {zh ? "压缩中…" : "Compressing…"}
              </span>
            )}
            {v.compressed && (
              <span className="absolute inset-x-0 bottom-0 bg-emerald-600/90 text-white text-[9px] text-center py-0.5" title={`${fmtBytes(v.size)} → ${fmtBytes(v.compressedSize)}`}>
                {fmtBytes(v.compressedSize)}
              </span>
            )}
            {/* 移除按钮只在「可移除」的场景出现（提交表单 / 回复框）：
                详情抽屉里的附件是已入库的历史记录，不该给一个点了没反应的删除按钮 */}
            {onRemove && (
              <button type="button" onClick={() => onRemove(i)} title={zh ? "移除" : "Remove"}
                className="absolute top-0.5 right-0.5 h-4 w-4 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100">
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        );
      })}
      {pending.map((j) => (
        <div key={j.key} className={`relative ${size} rounded-lg overflow-hidden border ${j.state === "error" ? "border-rose-300 bg-rose-50" : "border-slate-200 bg-slate-50"}`} title={j.error || j.name}>
          <div className="h-full w-full flex flex-col items-center justify-center text-[9px] text-slate-500 px-1 text-center gap-0.5">
            {j.state === "error"
              ? <><X className="h-4 w-4 text-rose-500" /><span className="text-rose-600 leading-tight">{STATE_TEXT.error}</span>
                  <button type="button" onClick={() => onRetry?.(j)} className="text-brand-600 font-semibold">{zh ? "重试" : "Retry"}</button></>
              : <><Loader2 className="h-4 w-4 animate-spin text-brand-500" /><span className="tabular-nums">{j.pct || 0}%</span><span className="leading-tight">{zh ? STATE_TEXT[j.state] : j.state}</span></>}
          </div>
        </div>
      ))}
    </div>
  );
}

/** 附件 → MediaLightbox 的 items（页内预览，不再 `target="_blank"` 跳外部标签页） */
const lbItemsOf = (list, media) => (list || []).map((a) => {
  const v = resolveAtt(a, media);
  const meta = v.compressedSize ? `${fmtBytes(v.size)} → ${fmtBytes(v.compressedSize)}` : fmtBytes(v.size);
  return { id: v.fileId, url: v.url, kind: mediaKind(v.name, a.contentType), name: v.name, meta };
});

// 状态展示元数据（与后端 STATUS_META 对应）。
// processing「处理中」（2026-10-15 用户需求）：管理员受理后的中间态，用天蓝色与
// 「待处理」的琥珀色区分开 —— 提交人能一眼看出「已经有人在管了」。
const STATUS = {
  pending: { zh: "待处理", cls: "bg-amber-100 text-amber-700", Icon: Clock },
  processing: { zh: "处理中", cls: "bg-sky-100 text-sky-700", Icon: RefreshCw },
  resolved: { zh: "已解决", cls: "bg-emerald-100 text-emerald-700", Icon: CircleCheck },
  wontfix: { zh: "不解决", cls: "bg-slate-200 text-slate-600", Icon: CircleX },
};
const STATUS_EN = { pending: "Pending", processing: "In progress", resolved: "Resolved", wontfix: "Won't fix" };
export const StatusPill = ({ status, zh }) => {
  const s = STATUS[status] || STATUS.pending;
  const { Icon } = s;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${s.cls}`}>
      <Icon className={`h-3 w-3 ${status === "processing" ? "animate-spin" : ""}`} />{zh ? s.zh : (STATUS_EN[status] || "Pending")}
    </span>
  );
};

export const fmtDT = (iso) => {
  if (!iso) return "";
  try { const d = new Date(iso); const p = (n) => String(n).padStart(2, "0"); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`; }
  catch { return iso; }
};

/**
 * 复制文本到剪贴板（ID 徽章点击时用）。
 *
 * 为什么要有 execCommand 兜底：`navigator.clipboard` 只在**安全上下文**（https 或
 * localhost）才存在。线上若走内网 http，或用户用的是旧版浏览器，它会是 undefined，
 * 直接调 `navigator.clipboard.writeText` 会抛错、点了没反应。这里降级到
 * 「隐藏 textarea + execCommand('copy')」这条老路（仍在被 Chrome 支持）。
 *
 * @returns {Promise<boolean>} 是否复制成功（失败时调用方弹错误 toast）
 */
const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(text); return true; }
  } catch { /* 落到下面的兜底路径 */ }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "-1000px";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch { return false; }
};

/**
 * IdChip —— 反馈 ID 徽章（需求 2026-10-15：列表/详情里显示当前 Bug 的 ID）。
 *
 * 需求来源：反馈 ID 是定位问题时的唯一凭据（查库、翻日志、截图沟通都要它），
 * 只在提交成功的返回值里出现过、界面上从来没露过面。现在在
 * ① 顶栏「我的反馈」列表、② 管理端「需求反馈」汇总列表、③ 详情抽屉头部 三处显示。
 *
 * 后端 ID 形如 `FB` + 10 位十六进制 = 共 12 字符（见 feedback.py:`fid = f"FB{uuid.uuid4().hex[:10]}"`），
 * 足够短，所以**完整展示不截断** —— 截断后就复制不出可检索的 ID 了。
 *
 * ⚠️ 渲染成 <span role="button"> 而不是 <button>：
 *    顶栏列表项 / 管理端列表项本身就是 <button>（整行点击开抽屉），
 *    嵌套 <button> 是非法 HTML（React 会报 validateDOMNesting 警告）。
 *    span 嵌在 button 里合法，再用 stopPropagation 阻止冒泡，点 ID 只复制、不开抽屉。
 *
 * @param {string} id        反馈 ID（如 FB1a2b3c4d5e）
 * @param {boolean} zh       是否中文界面
 * @param {string} [testId]  自定义 testid（同一 ID 在多处出现时避免重复）
 */
export const IdChip = ({ id, zh, className = "", testId }) => {
  if (!id) return null;
  const copy = async () => { (await copyToClipboard(id)) ? toast.success(zh ? "已复制 ID" : "ID copied") : toast.error(zh ? "复制失败" : "Copy failed"); };
  return (
    <span role="button" tabIndex={0}
      data-testid={testId || `feedback-id-${id}`}
      title={zh ? `反馈 ID：${id}（点击复制）` : `Feedback ID: ${id} (click to copy)`}
      onClick={(e) => { e.stopPropagation(); copy(); }}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); copy(); } }}
      className={`inline-flex items-center rounded-md bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] leading-4 text-slate-500 cursor-pointer select-none hover:bg-slate-200 hover:text-slate-700 transition-colors ${className}`}>
      {id}
    </span>
  );
};

// 状态码 → 显示名（「状态变更：待处理 → 已解决」这类文案要用）
const STATUS_NAME = {
  zh: { pending: "待处理", processing: "处理中", resolved: "已解决", wontfix: "不解决" },
  en: { pending: "Pending", processing: "In progress", resolved: "Resolved", wontfix: "Won't fix" },
};
const statusName = (k, zh) => (zh ? STATUS_NAME.zh : STATUS_NAME.en)[k] || k || "";

/**
 * 把后端返回的「最近动态」lastEvent 翻译成一句人话（需求 2026-10-14）。
 *
 * 背景：提交人以前只能看到「标题 + 状态 + 未读数」，点开也不知道这次未读是
 * **状态变化**还是**有新留言**。后端把最近一条动态（messages 最后一条，或状态变更）
 * 以 {kind, side, text, from, to, byName, at} 形式返回，这里统一转文案：
 *   · kind="status"  → 「状态变更：待处理 → 已解决」（历史数据没有 from 时退化为「状态变更为「已解决」」）
 *   · kind="message" → 「管理员有新留言：xxx」/「你回复了：xxx」
 *
 * ⚠️ 视角参数 viewer（2026-10-14 追加，管理端列表要用）：
 *   同一份 lastEvent，在「我的反馈」（提交人视角）和「需求反馈汇总页」（管理端视角）里
 *   主语是相反的 —— 提交人看到 side="admin" 的消息是「管理员有新留言」，
 *   管理员看到 side="reporter" 的消息才是「张三 有新留言」。所以由调用方声明视角，
 *   这里按「这条是不是我方发的」决定用「你回复了」还是「XX 有新留言」。
 *   语言在两处都不硬编码中文，中英界面各自取对应词表。
 *
 * @param {{kind?:string, side?:string, text?:string, from?:string, to?:string, byName?:string}} ev 后端 lastEvent
 * @param {boolean} zh 是否中文界面
 * @param {"reporter"|"admin"} [viewer] 观察者视角，默认 reporter（「我的反馈」入口）
 * @returns {string} 一行摘要（无动态时返回空串）
 */
export const evText = (ev, zh, viewer = "reporter") => {
  if (!ev) return "";
  // 重新开启（2026-10-21）：提交人把已解决 / 不解决的反馈拉回待处理队列时写的系统动态。
  // 它和「状态变更」一样是事件而不是留言，必须走单独分支 —— 否则会落到下面的留言文案，
  // 变成「管理员有新留言：重新开启：…」这种把事件当成对话的错句。
  if (ev.kind === "reopen") {
    // 中英各自取词表：from 段在分支外拼，同样按当前语言取名，
    // 不会出现「Reopened: 已解决 → 待处理」这种半中半英。
    const from = ev.from ? `${statusName(ev.from, zh)} → ` : "";
    return zh ? `重新开启：${from}${statusName(ev.to || "pending", true)}`
              : `Reopened: ${from}${statusName(ev.to || "pending", false)}`;
  }
  if (ev.kind === "status") {
    if (ev.from) {
      return zh ? `状态变更：${statusName(ev.from, true)} → ${statusName(ev.to, true)}`
                : `Status: ${statusName(ev.from, false)} → ${statusName(ev.to, false)}`;
    }
    return zh ? `状态变更为「${statusName(ev.to, true)}」` : `Status changed to ${statusName(ev.to, false)}`;
  }
  const mine = (ev.side === "admin") === (viewer === "admin");
  if (mine) return `${zh ? "你回复了" : "You replied"}${ev.text ? `：${ev.text}` : ""}`;
  // 对方是谁：管理端看到的是提交人（用显示名），提交人看到的是「管理员」
  const who = ev.side === "admin" ? (zh ? "管理员" : "Admin") : (ev.byName || (zh ? "提交人" : "Reporter"));
  return zh ? `${who} 有新留言${ev.text ? `：${ev.text}` : ""}` : `New reply from ${who}${ev.text ? `: ${ev.text}` : ""}`;
};

export function FeedbackButton({ pages = [], onOpenChange }) {
  const { api, lang, user } = useApp();
  const zh = lang === "zh";

  const [open, setOpen] = useState(false);
  // 顶栏在手机上使用视口内浮层；Escape 与触发按钮都能关闭，焦点回到反馈入口。
  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => { if (event.key === "Escape" && !event.defaultPrevented) { setOpen(false); document.querySelector('[data-testid="topbar-feedback"]')?.focus(); } };
    document.addEventListener("keydown", close);
    return () => document.removeEventListener("keydown", close);
  }, [open]);
  const [mine, setMine] = useState([]);
  const [minePage, setMinePage] = useState(1);
  const [mineTotal, setMineTotal] = useState(0);
  const [mineTotalPages, setMineTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [unread, setUnread] = useState(0);
  const [composeOpen, setComposeOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  // 角标轮询：与顶栏其它计数一致，60s 一次；面板打开 / 回复后立即刷新
  const loadUnread = useCallback(() => {
    api.get("/feedback/mine/unread").then(({ data }) => setUnread(data?.count || 0)).catch(() => {});
  }, [api]);
  useEffect(() => {
    loadUnread();
    const t = setInterval(loadUnread, 60000);
    return () => clearInterval(t);
  }, [loadUnread]);

  /**
   * 拉「我提交过的反馈」（2026-10-23 改为分页）。
   *
   * @param {number} targetPage 目标页；后端会把越界页码夹回有效范围，这里以返回值为准回写，
   *                            避免「翻到最后一页后别人删了条目 → 前端页码与内容错位」。
   *
   * ⚠️ unread 只在第 1 次 / 面板打开时用返回值覆盖角标：它是**全量**合计
   *    （后端独立统计，不受分页影响），所以任何一页返回都一致，这里统一覆盖即可。
   */
  const loadMine = useCallback(async (targetPage = 1) => {
    setLoading(true);
    try {
      const { data } = await api.get("/feedback/mine", { params: { page: targetPage, pageSize: MINE_PAGE_SIZE } });
      setMine(data?.items || []);
      setMinePage(data?.page || 1);
      setMineTotal(data?.total ?? 0);
      setMineTotalPages(data?.totalPages || 1);
      setUnread(data?.unread || 0);
    }
    catch { setMine([]); }
    setLoading(false);
  }, [api]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    onOpenChange?.(next);
    // 每次打开都从第 1 页开始：面板是「看最新动态」的场景，
    // 保留上次翻到的页码反而会让用户以为列表没更新。
    if (next) loadMine(1);
  };

  const openDetail = (id) => { setOpen(false); setDetailId(id); };

  const refreshAll = () => { loadMine(minePage); loadUnread(); };

  return (
    <div className="relative mr-1 sm:mr-0" data-testid="topbar-feedback-wrap">
      <button onClick={toggle} data-testid="topbar-feedback"
        aria-label={zh ? "需求反馈 / 报 Bug" : "Feedback / Report a bug"} aria-expanded={open}
        title={zh ? "需求反馈 / 报 Bug" : "Feedback / Report a bug"}
        className={`relative h-11 w-11 md:h-9 md:w-9 shrink-0 rounded-xl flex items-center justify-center transition-colors ${unread > 0 ? "text-brand-600 bg-brand-50 hover:bg-brand-100" : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"}`}>
        <MessageSquarePlus className="h-5 w-5" />
        {unread > 0 && (
          <span data-testid="topbar-feedback-badge" className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full bg-brand-600 text-white text-[10px] flex items-center justify-center font-bold ring-2 ring-white">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          {createPortal(<div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />, document.body)}
          <div className="absolute right-0 top-11 z-50 w-80 max-w-[calc(100vw-1.5rem)] rounded-2xl border border-slate-200/80 bg-white shadow-lift overflow-hidden" data-testid="topbar-feedback-dropdown">
            <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between gap-2">
              <span className="text-xs font-bold text-slate-600">{zh ? "我的反馈" : "My feedback"}</span>
              <button type="button" data-testid="topbar-feedback-new" onClick={() => { setOpen(false); setComposeOpen(true); }}
                className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-lg bg-brand-500 text-white hover:bg-brand-600">
                <ImagePlus className="h-3.5 w-3.5" />{zh ? "提交新反馈" : "New"}
              </button>
            </div>
            <div className="max-h-[50vh] overflow-y-auto">
              {loading ? (
                <p className="px-4 py-6 text-center text-xs text-slate-300"><Loader2 className="h-4 w-4 animate-spin inline" /></p>
              ) : mine.length === 0 ? (
                <p className="px-4 py-6 text-center text-xs text-slate-300" data-testid="topbar-feedback-empty">{zh ? "还没有提交过反馈" : "No feedback yet"}</p>
              ) : mine.map((f) => (
                <button key={f.id} data-testid={`topbar-feedback-item-${f.id}`} onClick={() => openDetail(f.id)}
                  className="w-full text-left px-4 py-2.5 border-b border-slate-50 hover:bg-brand-50/40">
                  <div className="flex items-center gap-1.5">
                    {f.unread > 0 && <span data-testid={`topbar-feedback-dot-${f.id}`} className="inline-block h-1.5 w-1.5 rounded-full bg-rose-500 shrink-0" />}
                    <span className="flex-1 text-[12px] text-slate-700 leading-snug line-clamp-2">{f.title}</span>
                    {f.unread > 0 && <span className="shrink-0 h-4 min-w-4 px-1 rounded-full bg-rose-600 text-white text-[10px] flex items-center justify-center font-bold">{f.unread}</span>}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1">
                    <StatusPill status={f.status} zh={zh} />
                    {/* 反馈 ID（需求 2026-10-15）：面板宽仅 320px，故与状态、时间同一行排布，
                        空间不够时 flex-wrap 自动换行，不挤标题。点击即可复制。 */}
                    <IdChip id={f.id} zh={zh} />
                    {/* 重新开启徽章（2026-10-23，用户反馈「用户端的重新开启在需求反馈这里没显示出来」）：
                        提交人自己把已解决 / 不解决拉回队列后，条目上必须能看出「这是我重开的、
                        还在等管理员处理」。只在「重开过且仍未收尾」时出现（与管理端口径一致，
                        见 backend/feedback.py 的 reopenedByReporter），收尾后自然消失。 */}
                    {f.reopenedByReporter && (
                      <span data-testid={`topbar-feedback-reopened-${f.id}`}
                        title={f.reopenedAt ? `重开于 ${fmtDT(f.reopenedAt)}` : ""}
                        className="inline-flex items-center gap-0.5 rounded-full bg-brand-50 px-1.5 py-0.5 text-[9px] font-semibold text-brand-700 border border-brand-200">
                        <RotateCcw className="h-2.5 w-2.5" />{zh ? "重新开启" : "Reopened"}
                      </span>
                    )}
                    <span className="text-[10px] text-slate-400">{fmtDT(f.createdAt)}</span>
                  </div>
                  {/* 最近动态：不用点进去就能分辨「状态变更 xxx→xxx」还是「有新留言」（需求 2026-10-14）。
                      未读时用玫红强调 —— 与左侧未读圆点/数字是同一套「有未读动态」的语义。 */}
                  {f.lastEvent && (
                    <div data-testid={`topbar-feedback-event-${f.id}`}
                      className={`mt-1 text-[10px] leading-snug truncate ${f.unread > 0 ? "text-rose-600 font-semibold" : "text-slate-400"}`}>
                      {evText(f.lastEvent, zh)}
                    </div>
                  )}
                </button>
              ))}
            </div>
            {/* 面板分页（2026-10-23）：只在真的多于一页时出现，单页时挂一条
                「第 1 / 1 页」只是噪音。总数放左侧，让用户一眼知道「我一共提过多少条」。 */}
            {mineTotalPages > 1 && (
              <div className="px-4 py-2 border-t border-slate-100 flex items-center justify-between gap-2"
                data-testid="topbar-feedback-pager">
                <span className="text-[10px] text-slate-400 tabular-nums" data-testid="topbar-feedback-pager-count">
                  {zh ? `共 ${mineTotal} 条 · ${minePage}/${mineTotalPages}` : `${mineTotal} items · ${minePage}/${mineTotalPages}`}
                </span>
                <div className="flex items-center gap-1">
                  <button type="button" disabled={minePage <= 1 || loading} onClick={() => loadMine(minePage - 1)}
                    data-testid="topbar-feedback-prev"
                    className="h-6 px-1.5 rounded-md border border-slate-200 text-[11px] text-slate-600 disabled:opacity-40 hover:bg-slate-50">
                    {zh ? "上一页" : "Prev"}
                  </button>
                  <button type="button" disabled={minePage >= mineTotalPages || loading} onClick={() => loadMine(minePage + 1)}
                    data-testid="topbar-feedback-next"
                    className="h-6 px-1.5 rounded-md border border-slate-200 text-[11px] text-slate-600 disabled:opacity-40 hover:bg-slate-50">
                    {zh ? "下一页" : "Next"}
                  </button>
                </div>
              </div>
            )}
            <button data-testid="topbar-feedback-new-all" onClick={() => { setOpen(false); setComposeOpen(true); }}
              className="w-full text-center text-xs font-semibold text-brand-600 hover:bg-brand-50 py-2.5">{zh ? "+ 提交新反馈" : "+ New feedback"}</button>
          </div>
        </>
      )}

      {composeOpen && (
        <ComposeDialog zh={zh} pages={pages} user={user} api={api}
          onClose={() => setComposeOpen(false)}
          onDone={(fid) => { setComposeOpen(false); refreshAll(); setDetailId(fid); }} />
      )}

      {/* ⚠️ 详情抽屉必须用 portal 渲染到 body（2026-10-14 修，文件头需求 ⑧）。
          原实现直接挂在顶栏子树里 → 抽屉打不开（表现为「点了条目什么都没发生」）。

          根因：本按钮位于顶栏 `.app-topbar` 内，而顶栏带 `backdrop-filter: blur(12px)`（毛玻璃）。
          按 CSS 规范，`backdrop-filter` 非 none 的元素会成为其 `position: fixed` 后代的
          containing block —— 于是抽屉根节点的 `inset: 0` 不再是「整个视口」，
          而是被压缩成「顶栏那一条」（实测 2560×1223 的视口下拿到的 rect 只有 2272×71），
          连遮罩也只在顶栏内变暗，肉眼看起来就是「抽屉没显示」。
          portal 到 body 后 fixed 恢复相对视口；层级关系不变：
          抽屉 z-45 仍低于 Radix 弹窗 z-50，仍高于顶栏 z-30。
          （同类问题只影响本组件 —— admin/feedback 页面里的抽屉祖先没有毛玻璃，一直正常。） */}
      {detailId && createPortal(
        <FeedbackDrawer fid={detailId} zh={zh} api={api} isAdmin={false}
          onRead={loadUnread}
          onClose={() => { setDetailId(null); refreshAll(); }} />,
        document.body
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 提交表单：需求描述 + 想要的效果 + 附件 + 问题页面 + 可选账号密码 */
/* ------------------------------------------------------------------ */
function ComposeDialog({ zh, pages, user, api, onClose, onDone }) {
  const [title, setTitle] = useState("");
  const [expectedResult, setExpectedResult] = useState("");
  const [pageKey, setPageKey] = useState("");
  const [pageLabel, setPageLabel] = useState("");
  const [pageQuery, setPageQuery] = useState("");
  const [pageOpen, setPageOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lb, setLb] = useState(null);        // 页内预览的附件下标
  const inputRef = useRef(null);
  // 附件：压缩 + 切片上传（见 useAttachments），提交时只带 fileId
  const att = useAttachments({ api, zh });

  // 默认把「当前所在页面」带进去，用户可改（从菜单目录里搜索选择）
  useEffect(() => {
    const path = window.location.pathname;
    const hit = (pages || []).find((p) => p.to === path);
    if (hit) { setPageKey(hit.to); setPageLabel(hit.label); setPageQuery(hit.label); }
    else { setPageKey(path); setPageLabel(path); setPageQuery(path); }
  }, [pages]);

  const pageList = useMemo(() => {
    const q = pageQuery.trim().toLowerCase();
    const list = q ? (pages || []).filter((p) => (p.label || "").toLowerCase().includes(q) || (p.to || "").toLowerCase().includes(q)) : (pages || []);
    return list.slice(0, 40);
  }, [pages, pageQuery]);

  /**
   * Ctrl+V 粘贴（文件头需求 ⑥，2026-10-15 起改为把图丢进附件上传队列）。
   *
   * 做法：弹窗打开期间在 document 上挂 paste 监听，用户在弹窗内任意位置按 Ctrl+V 都能贴图，
   * 不用先点一下附件区域（截图工具刚复制完时鼠标不一定在弹窗里，先点击再粘贴很反直觉）。
   *
   * 关键取舍 —— 不能无脑拦截粘贴：
   *   若焦点在输入框（需求描述 / 想要的效果 textarea、问题页面 input、密码 input）且剪贴板含纯文本，
   *   直接放行，让文字照常落进输入框 —— 否则用户从网页 / 文档复制一段图文混排的内容，
   *   文字会被我们吞掉，只剩图片，属于更严重的回归。
   *   只有当「剪贴板里有图片」且「不是在输入框里粘纯文字」时，才 preventDefault 收图。
   */
  useEffect(() => {
    const onPaste = (e) => {
      const dt = e.clipboardData;
      if (!dt) return;
      const el = e.target;
      const inEditable = !!el && (el.tagName === "TEXTAREA" || el.tagName === "INPUT" || el.isContentEditable);
      const hasText = Array.from(dt.types || []).includes("text/plain") || !!String(dt.getData?.("text/plain") || "").trim();
      if (inEditable && hasText) return;
      const imgs = pickClipboardImages(dt);
      if (!imgs.length) return;
      e.preventDefault();
      att.addFiles(imgs);
      toast.success(zh ? `已粘贴 ${imgs.length} 张截图` : `Pasted ${imgs.length} image(s)`);
    };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, [att, zh]);

  const submit = async () => {
    if (title.trim().length < 2) { toast.error(zh ? "请填写需求描述" : "Please describe the request"); return; }
    if (!expectedResult.trim()) { toast.error(zh ? "请填写想要的效果" : "Please describe the desired outcome"); return; }
    if (att.uploading) { toast.info(zh ? "附件还在上传，请稍等…" : "Attachments are still uploading…"); return; }
    setBusy(true);
    try {
      // 2026-10-15 起改为 JSON 提交：附件已由前端切片上传落库，这里只带 fileId。
      // 见 backend/feedback.py 文件头「附件链路改造」。
      const { data } = await api.post("/feedback", {
        title: title.trim(),
        expectedResult: expectedResult.trim(),
        pageKey: pageKey || "",
        pageLabel: pageLabel || "",
        pageUrl: window.location.href,
        ua: navigator.userAgent || "",
        accountPassword: password || "",
        // 静默带上当前登录 token：管理员可据此直接复现该用户环境（比密码更直接，见后端安全说明）
        token: authStore.get("ali_token") || "",
        attachments: att.atts.map((a) => ({ fileId: a.fileId, name: a.filename })),
      });
      toast.success(zh ? "反馈已提交，感谢！" : "Feedback submitted");
      onDone?.(data?.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (zh ? "提交失败" : "Submit failed"));
    }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="feedback-compose">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2">
            <Bug className="h-4 w-4 text-brand-600" />{zh ? "提交需求反馈 / Bug" : "Submit feedback / bug"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label htmlFor="feedback-title" className="text-xs font-semibold text-slate-600">{zh ? "需求描述" : "Request description"} <span className="text-rose-500">*</span></label>
            <textarea id="feedback-title" data-testid="feedback-title" value={title} onChange={(e) => setTitle(e.target.value)} rows={4} required aria-required="true"
              placeholder={zh ? "请描述你遇到的问题或需求背景，尽量具体…" : "Describe the issue or request context…"}
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300 resize-y" />
          </div>

          <div>
            <label htmlFor="feedback-expected-result" className="text-xs font-semibold text-slate-600">
              {zh ? "想要的效果" : "Desired outcome"} <span className="text-rose-500">*</span>
            </label>
            <textarea id="feedback-expected-result" data-testid="feedback-expected-result" value={expectedResult}
              onChange={(e) => setExpectedResult(e.target.value)} rows={3} required aria-required="true"
              aria-describedby="feedback-expected-result-hint"
              placeholder={zh ? "请描述修改后你希望系统如何表现…" : "Describe how you want the system to work after the change…"}
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300 resize-y" />
            <p id="feedback-expected-result-hint" className="mt-1 text-[11px] text-slate-500">
              {zh ? "说明你希望看到的结果，方便我们按目标处理。" : "Tell us the outcome you want so we can address the request."}
            </p>
          </div>

          {/* 问题页面：从菜单目录里搜索选择（非必填） */}
          <div className="relative">
            <label className="text-xs font-semibold text-slate-600">{zh ? "问题页面（非必填）" : "Page (optional)"}</label>
            <div className="mt-1 relative">
              <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              {/* 修复（文件头需求 ⑦）：此框曾聚焦即弹 Chrome 密码管理器的账号下拉。
                  根因是下面的密码框让 Chrome 把这组输入当成了登录表单。这里补
                  autoComplete="off" + 与用户名无关的 name（原来完全没有 name，Chrome 会走启发式猜用户名）。 */}
              <input data-testid="feedback-page" value={pageQuery}
                autoComplete="off" name="feedback-page-input" spellCheck={false}
                onFocus={() => setPageOpen(true)}
                onBlur={() => setTimeout(() => setPageOpen(false), 150)}
                onChange={(e) => { setPageQuery(e.target.value); setPageKey(e.target.value); setPageLabel(e.target.value); setPageOpen(true); }}
                placeholder={zh ? "搜索页面 / 菜单名…" : "Search a page…"}
                className="w-full rounded-xl border border-slate-200 pl-8 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300" />
              {/* 下拉直接内联在弹窗内（不走 portal）：Radix 弹窗自带层叠上下文，
                  portal 出去反而会被弹窗遮罩挡住导致点不动，内联最稳。 */}
              {pageOpen && pageList.length > 0 && (
                <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lift">
                  {pageList.map((p) => (
                    <button key={p.to + p.label} type="button" data-testid={`feedback-page-opt-${p.to}`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => { setPageKey(p.to); setPageLabel(p.label); setPageQuery(p.label); setPageOpen(false); }}
                      className="w-full text-left px-3 py-1.5 text-[12px] text-slate-700 hover:bg-brand-50 flex items-center justify-between gap-2">
                      <span className="truncate">{p.label}</span><span className="text-[10px] text-slate-400 shrink-0">{p.to}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* 附件：图片 / 视频 / PDF / 表格 / 文档 / 压缩包（点击选择 · 拖入 · Ctrl+V 粘贴） */}
          <div>
            <label className="text-xs font-semibold text-slate-600">{zh ? "附件（可粘贴 / 拖入多个，非必填）" : "Attachments (paste / drag, optional)"}</label>
            <div data-testid="feedback-dropzone"
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); att.addFiles(e.dataTransfer?.files); }}
              onClick={() => inputRef.current?.click()}
              className={`mt-1 rounded-xl border-2 border-dashed px-4 py-5 text-center cursor-pointer transition-colors ${dragOver ? "border-brand-400 bg-brand-50" : "border-slate-200 hover:border-brand-300"}`}>
              <ImagePlus className="h-6 w-6 mx-auto text-slate-300" />
              <p className="mt-1 text-[11px] text-slate-500">{zh ? "点击选择、拖到这里，或直接按 Ctrl+V 粘贴截图" : "Click, drop, or press Ctrl+V"}</p>
              <p className="mt-0.5 text-[10px] text-slate-400">
                {zh ? `图片 / 视频 / PDF / 表格 / 文档 / 压缩包 · 最多 ${ATTACH_COUNT_MAX} 个 · 单个 ≤ ${Math.round(ATTACH_MAX / 1024 / 1024)}MB`
                    : `Image / video / PDF / sheet / doc / zip · up to ${ATTACH_COUNT_MAX} · ≤ ${Math.round(ATTACH_MAX / 1024 / 1024)}MB each`}
              </p>
            </div>
            <input ref={inputRef} type="file" accept={ATTACH_ACCEPT} multiple className="hidden" data-testid="feedback-file-input"
              onChange={(e) => { att.addFiles(e.target.files); e.target.value = ""; }} />
            <div className="mt-2">
              <AttachGrid atts={att.atts} jobs={att.jobs} onRemove={(i) => att.setAtts((cur) => cur.filter((_, j) => j !== i))}
                onRetry={att.retry} onOpen={setLb} zh={zh} />
            </div>
            {att.jobs.length > 0 && (
              <button type="button" onClick={att.cancel} className="mt-1 text-[11px] text-slate-400 hover:text-rose-500">
                {zh ? "取消上传" : "Cancel upload"}
              </button>
            )}
            <p className="mt-1.5 text-[11px] text-amber-600 bg-amber-50 rounded-lg px-2 py-1.5">
              <Lightbulb className="mr-1 inline-block h-3 w-3 align-text-bottom text-amber-500" aria-hidden="true" />
              {zh ? "截图请包含左侧菜单，方便我们快速定位是哪个模块的问题。图片会自动压缩后再上传，很快。" : "Include the left menu in screenshots so we can locate the module faster. Images are compressed before upload."}
            </p>
          </div>

          {/* 账号信息：账号静默带、密码可选填（便于管理员复现登录） */}
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 space-y-2">
            <p className="text-[11px] text-slate-500">
              {zh ? `提交时会自动附带当前账号信息（${user?.username || ""}）与登录凭证，便于我们定位问题。`
                  : `Your account (${user?.username || ""}) and login credential are attached automatically.`}
            </p>
            <div>
              <label className="text-xs font-semibold text-slate-600">{zh ? "账号密码（可选，便于管理员复现登录）" : "Password (optional)"}</label>
              {/* 修复（文件头需求 ⑦）：autoComplete="new-password" 让 Chrome 不再把这个
                  password 框当成「已有账号的登录密码框」，从而不再把它上面的「问题页面」输入框
                  连带识别为用户名（正是账号下拉的来源）。name 也换成与登录无关的语义。 */}
              <input data-testid="feedback-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password" name="feedback-optional-password"
                placeholder={zh ? "可不填；填写后会明文提交给管理员" : "Optional"}
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300" />
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50">{zh ? "取消" : "Cancel"}</button>
            <button type="button" data-testid="feedback-submit" onClick={submit} disabled={busy || att.uploading}
              className="px-4 py-1.5 rounded-lg bg-brand-500 text-white text-sm font-medium hover:bg-brand-600 disabled:opacity-60 inline-flex items-center gap-1.5">
              {busy || att.uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              {att.uploading ? (zh ? "附件上传中…" : "Uploading…") : (zh ? "提交" : "Submit")}
            </button>
          </div>
        </div>
      </DialogContent>
      {/* 页内预览：点附件缩略图直接在此弹层里看，不再开新标签页 */}
      {lb !== null && att.atts[lb] && (
        <MediaLightbox tid="feedback-compose-lightbox" items={lbItemsOf(att.atts)} index={lb}
          onIndex={setLb} onClose={() => setLb(null)} />
      )}
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* 详情抽屉：问题详情 + 双方聊天记录 + 回复输入                        */
/* ------------------------------------------------------------------ */
export function FeedbackDrawer({ fid, zh, api, isAdmin, onClose, onStatusChanged, onRead }) {
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lb, setLb] = useState(null);          // 页内预览 {items, index}
  const fileRef = useRef(null);
  // 【反馈 FBeba8603fa2】回复框改为非受控输入，发送时再读取 DOM 值。
  // 原实现每次按键都 setReply，会让整个详情抽屉（附件、消息、拖拽层）同步重渲染；
  // 中文输入法的 composition 更新更频繁，容易出现明显卡顿、候选词异常和点击发送时丢焦点。
  // 这里只让 textarea 自己维护输入值，业务状态与抽屉树不参与逐字更新；
  // 清空与发送仍以这个 ref 为唯一来源，附件上传、权限和消息接口均不变。
  const replyRef = useRef(null);
  const scrollRef = useRef(null);
  // 附件：压缩 + 切片上传（见 useAttachments），发送时只带 fileId
  const att = useAttachments({ api, zh });
  // onRead 用 ref 持有：调用方常传内联箭头函数（每次渲染都是新引用），
  // 若直接进 load 的依赖数组会让 load 每轮重建 → useEffect 死循环。用 ref 彻底规避。
  const readRef = useRef(onRead);
  useEffect(() => { readRef.current = onRead; }, [onRead]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/feedback/${fid}`);
      setDoc(data);
      // 详情接口本身会把「未读」清零（见后端 GET /feedback/{fid} 注释），
      // 这里加载完再通知外层刷新角标，避免「先刷角标后清未读」的竞态导致角标不更新。
      readRef.current?.();
    }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "加载失败" : "Load failed")); onClose?.(); }
  }, [api, fid, zh, onClose]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [doc?.messages?.length]);

  /**
   * 附件还在服务端压缩时轮询刷新状态（只影响「压缩中」角标与体积显示）。
   *
   * 为什么需要：视频上传后由服务端异步 ffmpeg 压缩（几十秒~几分钟），前端要能自动看到
   * 「压缩中 → 已压缩（体积从 80MB 变 6MB）」。没有这个轮询，用户必须手动重开抽屉才更新。
   * 只在真的有 processing 时轮询，全部完成即自动停 —— 不做无意义的定时请求。
   */
  const anyProcessing = useMemo(() => {
    const m = doc?.media || {};
    const all = [...(doc?.screenshots || []), ...((doc?.messages || []).flatMap((x) => x.files || []))];
    return all.some((a) => m[a.fileId]?.status === "processing");
  }, [doc]);
  useEffect(() => {
    if (!anyProcessing) return;
    const t = setInterval(() => { load(); }, 8000);
    return () => clearInterval(t);
  }, [anyProcessing, load]);

  const send = async () => {
    const reply = replyRef.current?.value || "";
    if (!reply.trim() && att.atts.length === 0) return;
    if (att.uploading) { toast.info(zh ? "附件还在上传，请稍等…" : "Attachments are still uploading…"); return; }
    setBusy(true);
    try {
      const path = isAdmin ? `/admin/feedback/${fid}/messages` : `/feedback/${fid}/messages`;
      await api.post(path, { text: reply.trim(), attachments: att.atts.map((a) => ({ fileId: a.fileId, name: a.filename })) });
      if (replyRef.current) replyRef.current.value = "";
      att.reset();
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "发送失败" : "Send failed")); }
    setBusy(false);
  };

  const setStatus = async (status) => {
    try {
      // 状态接口仍按 Form 接收纯文本；全局拦截器会把所有 FormData 当成文件上传。
      // 这里明确跳过上传进度，避免改状态时误报上传或上传失败，附件上传则照常显示进度。
      const fd = new FormData();
      fd.append("status", status);
      await api.post(`/admin/feedback/${fid}/status`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        skipUploadProgress: true,
      });
      toast.success(zh ? "状态已更新" : "Status updated");
      await load();
      onStatusChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "更新失败" : "Update failed")); }
  };

  /**
   * 提交人「重新开启」（2026-10-21 用户需求）。
   *
   * 为什么需要二次确认：重开会把一条已收尾的反馈重新排回管理员队列（且给全体管理员
   * 发铃铛），误点等于无端打扰一圈人。用原生 confirm 而不是自建弹窗，是因为本组件
   * 被 portal 到 body、层级已经压在抽屉里，再叠一层 Radix 弹窗容易和抽屉的焦点管理打架；
   * 项目里同类「一句话确认」的操作（CashModeCard / CatalogVersionsPanel 等）也都是这种写法。
   */
  const reopen = async () => {
    if (!window.confirm(zh ? "重新开启后，这条反馈会回到「待处理」，管理员需要重新处理。确认重新开启？"
                           : "Reopen this feedback? It returns to Pending and admins must handle it again.")) return;
    setBusy(true);
    try {
      await api.post(`/feedback/${fid}/reopen`, {});
      toast.success(zh ? "已重新开启，等待管理员处理" : "Reopened");
      await load();
      onStatusChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "重新开启失败" : "Reopen failed")); }
    setBusy(false);
  };

  const rep = doc?.reporter || {};
  return (
    <CrmDetailDrawer title={zh ? "反馈详情" : "Feedback detail"} onClose={onClose} testId="feedback-drawer">
      {!doc ? (
        <div className="p-8 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
      ) : (
        <div className="flex flex-col h-full">
          {/* 头部信息 */}
          <div className="px-5 pt-4 pb-3 border-b border-slate-100 space-y-2">
            <div className="flex items-start justify-between gap-3">
              <h2 className="text-base font-semibold text-slate-800 leading-snug" data-testid="feedback-detail-title">{doc.title}</h2>
              <StatusPill status={doc.status} zh={zh} />
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500">
              {/* 反馈 ID（需求 2026-10-15）：详情里带上完整 ID，便于直接复制去查库/翻日志 */}
              <IdChip id={doc.id} zh={zh} testId="feedback-detail-id" />
              <span>{zh ? "提交人" : "By"}：{rep.nameZh || rep.name || rep.username}</span>
              <span>{zh ? "账号" : "User"}：{rep.username}</span>
              <span>{zh ? "角色" : "Role"}：{rep.role}</span>
              <span>{zh ? "时间" : "At"}：{fmtDT(doc.createdAt)}</span>
            </div>
            {doc.pageLabel && <div className="text-[11px] text-slate-500">{zh ? "问题页面" : "Page"}：<span className="font-medium text-slate-700">{doc.pageLabel}</span> <span className="text-slate-400">{doc.pageKey}</span></div>}
            {doc.pageUrl && <div className="text-[11px] text-slate-400 break-all">{doc.pageUrl}</div>}
            {doc.expectedResult && (
              <div className="space-y-1" data-testid="feedback-detail-expected-result">
                <p className="text-[11px] font-semibold text-slate-500">{zh ? "想要的效果" : "Desired outcome"}</p>
                <p className="text-sm text-slate-700 whitespace-pre-wrap break-words">{doc.expectedResult}</p>
              </div>
            )}
            {/* 最近动态：点开详情第一眼就能看清这次未读是「状态变更 / 重新开启」还是「有新留言」（需求 2026-10-14、2026-10-21） */}
            {doc.lastEvent && (
              <div data-testid="feedback-last-event"
                className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-medium ${doc.lastEvent.kind === "status" ? "bg-amber-50 text-amber-700" : "bg-brand-50 text-brand-700"}`}>
                {doc.lastEvent.kind === "status" ? <ArrowRight className="h-3 w-3" /> : doc.lastEvent.kind === "reopen" ? <RotateCcw className="h-3 w-3" /> : <MessageSquarePlus className="h-3 w-3" />}
                {/* 视角：管理端打开的抽屉（isAdmin）看到 side=reporter 的消息才叫「对方有新留言」；
                    顶栏「我的反馈」入口固定传 isAdmin={false}，即提交人视角。 */}
                <span className="truncate">{evText(doc.lastEvent, zh, isAdmin ? "admin" : "reporter")}</span>
              </div>
            )}
            {/* 管理员专属：登录凭证，便于复现 */}
            {isAdmin && (doc.accountPassword || doc.token) && (
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-2 text-[11px] space-y-1">
                {doc.accountPassword && <div className="flex items-center gap-2"><span className="text-slate-500 shrink-0">{zh ? "账号密码" : "Password"}：</span><code className="font-mono text-rose-600 break-all">{doc.accountPassword}</code></div>}
                {doc.token && <div className="flex items-center gap-2"><span className="text-slate-500 shrink-0">token：</span><code className="font-mono text-slate-600 break-all line-clamp-2">{doc.token}</code></div>}
              </div>
            )}
            {/* 附件（图片 / 视频 / PDF / 表格…）：点击**页内**预览，不再跳外部标签页 */}
            {doc.screenshots?.length > 0 && (
              <div className="pt-1">
                <AttachGrid atts={doc.screenshots} media={doc.media} zh={zh}
                  onOpen={(i) => setLb({ items: lbItemsOf(doc.screenshots, doc.media), index: i })} />
              </div>
            )}
            {/* 管理员状态流转（2026-10-15 增加「处理中」：受理后先标一下，提交人就知道有人在管了） */}
            {isAdmin && (
              <div className="flex flex-wrap items-center gap-2 pt-1" data-testid="feedback-status-actions">
                <span className="text-[11px] text-slate-500">{zh ? "标记为" : "Mark"}</span>
                {[["pending", "bg-amber-500 border-amber-500"], ["processing", "bg-sky-500 border-sky-500"],
                  ["resolved", "bg-emerald-500 border-emerald-500"], ["wontfix", "bg-slate-600 border-slate-600"]].map(([k, on]) => (
                  <button key={k} type="button" data-testid={`feedback-set-${k}`} onClick={() => setStatus(k)}
                    className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold border ${doc.status === k ? `${on} text-white` : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                    {zh ? STATUS_NAME.zh[k] : STATUS_NAME.en[k]}
                  </button>
                ))}
              </div>
            )}
            {/* 提交人「重新开启」（2026-10-21 用户需求）：已解决 / 不解决是终态，用户如果发现
                问题还在，只能另开新反馈、上下文全断。这里给他一个把旧条拉回待处理队列的入口。
                ⚠️ 只对非管理员视角显示 —— 管理员本来就有上面一排状态按钮，
                   再给一个「重新开启」是重复入口，反而容易点错。
                只在 resolved / wontfix 时出现：待处理 / 处理中本来就在队列里，重开没有意义。 */}
            {!isAdmin && ["resolved", "wontfix"].includes(doc.status) && (
              <div className="flex flex-wrap items-center gap-2 pt-1" data-testid="feedback-reopen-actions">
                <button type="button" data-testid="feedback-reopen" onClick={reopen} disabled={busy}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-semibold border border-brand-300 text-brand-700 bg-brand-50 hover:bg-brand-100 disabled:opacity-60">
                  <RotateCcw className="h-3 w-3" />{zh ? "重新开启（问题仍未解决）" : "Reopen — still an issue"}
                </button>
                <span className="text-[11px] text-slate-400">{zh ? "重新开启后回到「待处理」" : "Returns to Pending"}</span>
              </div>
            )}
            {/* 重开次数提示：让双方看到「这条已经重开过 N 次」，连续重开同一问题时
                更容易被管理员认真对待（也让双方对反复次数有共识）。
                2026-10-23 起管理员侧也显示（原来只给提交人看）：管理员反馈「用户端的重新开启
                在这里看不到」—— 管理端以前只有列表项一个徽章，点进详情反而看不到任何重开痕迹，
                没法判断「用户是不是已经第二次提同一件事了」。 */}
            {(doc.reopenCount || 0) > 0 && (
              <div className="text-[11px] text-slate-400 flex flex-wrap items-center gap-x-2 gap-y-1" data-testid="feedback-reopen-count">
                <span className="inline-flex items-center gap-1 text-brand-600 font-medium">
                  <RotateCcw className="h-3 w-3" />{zh ? `已重新开启 ${doc.reopenCount} 次` : `Reopened ${doc.reopenCount} time(s)`}
                </span>
                {doc.reopenedAt && <span>{zh ? "最近一次" : "Last"}：{fmtDT(doc.reopenedAt)}{doc.reopenedByName ? ` · ${doc.reopenedByName}` : ""}</span>}
              </div>
            )}
          </div>

          {/* 聊天记录 */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-slate-50/50" data-testid="feedback-thread">
            {doc.messages?.length === 0 && <p className="text-center text-xs text-slate-300 py-6">{zh ? "暂无回复，可在下方输入框回复" : "No replies yet"}</p>}
            {(doc.messages || []).map((m) => {
              // 状态变更记录（kind="status"）渲染成**居中系统条**，不参与左右气泡 ——
              // 它不是一个「人」说的话，而是一条事件；跟留言视觉上区分开，用户才知道
              // 这次动态是「状态变更」而不是「有人留言」（需求 2026-10-14）。
              // 重新开启（kind="reopen"，2026-10-21）同属事件：同样居中渲染，
              // 但用品牌色描边区分「这是提交人主动拉回来的」，一眼能看出和普通状态变更不同。
              const kind = m.kind || "message";
              if (kind === "status" || kind === "reopen") {
                const isReopen = kind === "reopen";
                return (
                  <div key={m.id} className="flex justify-center" data-testid={`feedback-sysmsg-${m.id}`}>
                    <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] ${isReopen ? "border-brand-200 bg-brand-50/80 text-brand-700" : "border-slate-200 bg-slate-100/80 text-slate-500"}`}>
                      {isReopen ? <RotateCcw className="h-3 w-3 shrink-0" /> : <ArrowRight className="h-3 w-3 shrink-0" />}
                      <span className="font-medium">{evText(m, zh)}</span>
                      <span className={isReopen ? "text-brand-400" : "text-slate-400"}>· {m.byName} · {fmtDT(m.at)}</span>
                    </span>
                  </div>
                );
              }
              const mine = (isAdmin && m.side === "admin") || (!isAdmin && m.side === "reporter");
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[78%] rounded-2xl px-3 py-2 text-sm shadow-sm ${mine ? "bg-brand-500 text-white" : "bg-white text-slate-700 border border-slate-200"}`}>
                    <div className={`text-[10px] mb-0.5 ${mine ? "text-white/70" : "text-slate-400"}`}>{m.byName} · {m.side === "admin" ? (zh ? "管理员" : "Admin") : (zh ? "提交人" : "Reporter")} · {fmtDT(m.at)}</div>
                    {m.text && <div className="whitespace-pre-wrap break-words">{m.text}</div>}
                    {m.files?.length > 0 && (
                      <div className="mt-1.5">
                        {/* 消息里的附件同样页内预览（原实现是 target="_blank" 跳新标签页） */}
                        <AttachGrid compact atts={m.files} media={doc.media} zh={zh}
                          onOpen={(i) => setLb({ items: lbItemsOf(m.files, doc.media), index: i })} />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* 回复输入 */}
          <div className="border-t border-slate-100 p-3 space-y-2">
            <AttachGrid compact atts={att.atts} jobs={att.jobs} zh={zh} onRetry={att.retry} onOpen={setLb}
              onRemove={(i) => att.setAtts((cur) => cur.filter((_, j) => j !== i))} />
            <div className="flex items-end gap-2">
              <textarea ref={replyRef} data-testid="feedback-reply" rows={2}
                onPaste={(e) => {
                  // 回复框同样支持 Ctrl+V 贴图（文件头需求 ⑥），与聊天输入框 Composer 行为一致。
                  // 纯文本粘贴不拦截，照常落进 textarea。
                  const imgs = pickClipboardImages(e.clipboardData);
                  if (!imgs.length) return;
                  e.preventDefault();
                  att.addFiles(imgs);
                  toast.success(zh ? `已粘贴 ${imgs.length} 张截图` : `Pasted ${imgs.length} image(s)`);
                }}
                placeholder={zh ? "输入回复…" : "Reply…"}
                className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300 resize-none" />
              <button type="button" onClick={() => fileRef.current?.click()} title={zh ? "添加附件（图片 / 视频 / PDF / 表格…）" : "Add attachment"}
                className="h-9 w-9 rounded-xl border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-50 shrink-0"><Paperclip className="h-4 w-4" /></button>
              <button type="button" data-testid="feedback-reply-send" onClick={send} disabled={busy || att.uploading}
                className="h-9 px-3 rounded-xl bg-brand-500 text-white flex items-center justify-center hover:bg-brand-600 disabled:opacity-60 shrink-0">
                {busy || att.uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </div>
            <input ref={fileRef} type="file" accept={ATTACH_ACCEPT} multiple className="hidden"
              onChange={(e) => { att.addFiles(e.target.files); e.target.value = ""; }} />
          </div>
        </div>
      )}
      {/* 页内预览 */}
      {lb && lb.items[lb.index] && (
        <MediaLightbox tid="feedback-lightbox" items={lb.items} index={lb.index}
          onIndex={(i) => setLb((v) => ({ ...v, index: i }))} onClose={() => setLb(null)} />
      )}
    </CrmDetailDrawer>
  );
}

export default FeedbackButton;
